import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';

interface SalaryComponent {
  id: string;
  code: string;
  component_name: string;
  component_type: string;
  is_taxable: boolean;
  is_active: boolean;
}

const EMPTY_FORM = {
  code: '',
  componentName: '',
  componentType: 'FIXED_ALLOWANCE',
  calculationType: 'FIXED',
  isTaxable: false,
};

export default function SalaryComponents() {
  const [components, setComponents] = useState<SalaryComponent[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<SalaryComponent | null>(null);

  useEffect(() => {
    fetchComponents();
  }, []);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payroll/components');
      setComponents(response.data.components || []);
    } catch (error) {
      console.error('Failed to fetch salary components:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (comp: SalaryComponent) => {
    setEditingId(comp.id);
    setFormData({
      ...EMPTY_FORM,
      code: comp.code,
      componentName: comp.component_name,
      componentType: comp.component_type,
      isTaxable: comp.is_taxable,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/payroll/components/${editingId}`, formData);
      } else {
        await api.post('/payroll/components', formData);
      }
      setFormData(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      fetchComponents();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save salary component');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/payroll/components/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchComponents();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to deactivate component');
    }
  };

  const columns: Column<SalaryComponent>[] = [
    { key: 'code', header: 'Code' },
    { key: 'component_name', header: 'Component Name', sortable: true },
    { key: 'component_type', header: 'Type' },
    {
      key: 'is_taxable',
      header: 'Taxable',
      render: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${row.is_taxable ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
          {row.is_taxable ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => openEditForm(row)} className="text-blue-600 hover:underline text-sm">
            Edit
          </button>
          <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline text-sm">
            Deactivate
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Salary Components</h2>
        <Button onClick={openCreateForm}>Create Component</Button>
      </div>

      <Table columns={columns} rows={components} loading={loading} emptyMessage="No salary components yet" />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Component' : 'Create Component'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Component Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            required
            disabled={!!editingId}
          />
          <Input
            label="Component Name"
            value={formData.componentName}
            onChange={(e) => setFormData({ ...formData, componentName: e.target.value })}
            required
          />
          <Select
            label="Component Type"
            value={formData.componentType}
            onChange={(e) => setFormData({ ...formData, componentType: e.target.value })}
            options={[
              { value: 'BASIC', label: 'Basic Salary' },
              { value: 'FIXED_ALLOWANCE', label: 'Fixed Allowance' },
              { value: 'VARIABLE_ALLOWANCE', label: 'Variable Allowance' },
              { value: 'DEDUCTION', label: 'Deduction' },
            ]}
          />
          <Select
            label="Calculation Type"
            value={formData.calculationType}
            onChange={(e) => setFormData({ ...formData, calculationType: e.target.value })}
            options={[
              { value: 'FIXED', label: 'Fixed' },
              { value: 'PERCENTAGE', label: 'Percentage' },
              { value: 'FORMULA', label: 'Formula' },
            ]}
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.isTaxable}
              onChange={(e) => setFormData({ ...formData, isTaxable: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Is Taxable</span>
          </label>
          <Button type="submit" className="w-full">
            {editingId ? 'Save Changes' : 'Create Component'}
          </Button>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Deactivate Component"
        message={`Deactivate "${deleteTarget?.component_name}"?`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
